import React, { useRef, useState } from 'react';
import styles from './PropertyPremiumValuationBox.scss';
import useUser from '../stores/useUser';
import { IProperty } from '../utils/JsonApiRequest';
import ValuationApi from './Valuation/ValuationApi';
import PropertyRequest from '../utils/PropertyRequest';
import Translate from '../utils/Translate';
import DossierButton from './UserPage/DossierButton';
import LoadingIndicator from './LoadingIndicator';
import LockIcon from "../assets/img/icons/Lock.svg";
import { createModal } from '../utils/CreateModal';
import { useWindowDimensions } from './WindowDimensionsProvider';
import Icon from './MaterialUi/Icon';
import Button from './MaterialUi/Button';
import LogError from '../utils/LogError';

export interface IPropertyPremiumValuationBoxProps {
    uuid: IProperty['id'];
}




const PropertyPremiumValuationBox: React.FC<React.PropsWithChildren<IPropertyPremiumValuationBoxProps>> = ({ uuid = null }) => {

    const [user] = useUser();
    const [error, setError] = useState<string | null>(null);


    const isPremium = user.isPremium;

    const handleGetPremium = () => {
        if (user.isLoggedIn) {
            location.assign(`/user/${user.uid}/premium`);
        } else {
            createModal.createRegister((currUser) => {
                if (!currUser.is_premium) {
                    location.assign(`/user/${currUser.uid}/premium?destination=${window.location.pathname}`);
                }
            });
        }
    };



    return (
        <div className={[styles.propertyPremiumValuationBox, isPremium ? styles.premium : null].join(' ')} onClick={isPremium ? null : handleGetPremium}>
            <h5>Marktpreis-Vergleich</h5>
            <p className={styles.teaser}>Ist diese Immobilie überteuert?</p>
            <div className={styles.premiumLabel}>
                Premium
            </div>

            <PropertyPremiumValuationScale  uuid={uuid} setError={setError} isPremium={isPremium}/>
            
            <div className={styles.details}>
                {isPremium ? (
                    error ? (
                        <div className={styles.error}>{error}</div>
                    ) : (
                        <DossierButton variant="text" color="inherit" home={uuid} useExactLocation={false} endIcon={<Icon name="navigateNext" />}>
                            Exakten Marktpreis berechnen
                        </DossierButton>
                    )
                ) : (
                    <div className={styles.action}><Button onClick={handleGetPremium} variant="outlined" color="secondary" size="large">Mit Premium freischalten</Button></div>
                )}
            </div>
        </div>
    );
};

export interface IPropertyPremiumValuationScaleProps {
    uuid: IProperty['id'];
    setError?: (error: string) => void;
    isPremium?: boolean;
}

const fallbackSetError = (e) => {
    if (e) {
        console.error(e);
    }
}

export const PropertyPremiumValuationScale: React.FC<React.PropsWithChildren<IPropertyPremiumValuationScaleProps>> = ({ uuid = null, setError: setErrorFn, isPremium }) => {
    // const [user] = useUser();
    const [range, setRange] = React.useState<[number, number]>([null, null]);
    const [property, setProperty] = React.useState<IProperty>({} as IProperty);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const setError = setErrorFn || fallbackSetError;

    const { width } = useWindowDimensions();

    const barRef = useRef<HTMLDivElement>(null);
    const labelRef = useRef<HTMLDivElement>(null);

    // const isPremium = user.isPremium;

    React.useEffect(() => {
        if (isPremium) {
            setIsLoading(true);
            PropertyRequest.getProperty(uuid)
                .then((propertyData) => {

                    if (propertyData.field_address.country_code !== 'DE') {
                        setIsLoading(false);
                        setError('Der Marktwert kann nicht bestimmt werden, da sich die Immobilie nicht in Deutschland befindet.');
                        return;
                    }

                    ValuationApi.getValuatation(ValuationApi.property2ValuationApi(propertyData))
                        .then((data) => {
                            setProperty(propertyData);
                            setError(null);
                            let range = null;
                            try {
                                const valuation = data.valuations[0][0];
                                range =
                                    propertyData.type_of_marketing === 'buy'
                                        ? valuation?.salePriceRange
                                        : propertyData.price_label.toLowerCase() === 'kaltmiete'
                                            ? valuation?.rentNetRange
                                            : valuation?.rentGrossRange;
                            } catch (e) {
                                console.warn(e);
                            }
                            setIsLoading(false);
                            setRange([range?.lower, range?.upper]);
                        })
                        .catch((e: Error) => {
                            console.error(e);
                            setError('Der Marktwert kann nicht bestimmt werden.');
                            LogError.add(e, {
                                title: 'Error on getting property valuation',
                                error_code: 'PropertyRequest.tsx:getValuation',
                                request_data: { uuid, isPremium },
                            });
                            setIsLoading(false);
                        });
                })
                .catch((e) => {
                    console.error(e);
                    setError('Der Marktwert kann nicht bestimmt werden.');
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
        }
    }, [ isPremium, uuid]);



    // calculate label position and color
    const price = parseFloat(property.price);
    const left = range[0];
    const right = range[1];
    const labelWidth = Math.floor(labelRef.current?.offsetWidth) - 1 || 60;
    const barWidth = barRef.current?.offsetWidth || 800;
    const labelLeft = Math.max((labelWidth / 2) / barWidth * 100, Math.min(100 - (labelWidth / 2) / barWidth * 100, ((price - left) / (right - left)) * 100));

    const gradientLeft = Math.max(0, Math.min(100, ((price - left) / (right - left)) * 100));
    const l = -1 * gradientLeft * barWidth / 100;
    const gradient = isPremium
        ? `linear-gradient(90deg, #67c163 ${l * 10}px, #fcfd00 ${(l + barWidth / 2) * 10}px, #df3e37 ${(l + barWidth) * 10}px) 0% 0% no-repeat padding-box`
        : '#cccccc';

    return (
        <div className={styles.propertyValuationScale} ref={barRef}>
            {isLoading ? (
                <LoadingIndicator size="small" className={styles.loading} />
            ) : (
                <>
                    <div className={styles.lower}>{range[0] ? <>{Translate.getNumber(range[0])}  €</> : <LockIcon aria-hidden="true" />}</div>
                    <div className={styles.upper}>{range[1] ? <>{Translate.getNumber(range[1])}  €</> : <LockIcon aria-hidden="true" />}</div>
                    <div className={styles.bar}>
                        <div className={styles.price} style={{ left: isNaN(labelLeft) ? '50%' : labelLeft + '%', background: gradient }} ref={labelRef}>
                            {isPremium
                                ? <>{Translate.getNumber(price)} €</>
                                : <LockIcon aria-hidden="true" />
                            }
                        </div>
                    </div>
                </>
            )}
        </div>
             
    );
};

export default PropertyPremiumValuationBox;
